import type { PartnerStaff } from '../../models/partnerStaff.ts';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { Box, ActionIcon } from '@mantine/core';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useQueryClient } from '@tanstack/react-query';
import useUpdatePartnerStaff from '../../hooks/partner-staffs/useUpdatePartnerStaff.ts';
import type { UpdatePartnerStaffForm } from '../../components/modal/partner-staffs/UpdatePartnerStaffModal.tsx';
import useVoidPartnerStaff from '../../hooks/partner-staffs/useVoidPartnerStaff.ts';
import { notifySuccess } from '../../utils/NotifySuccess.tsx';
import { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import UpdatePartnerStaffModal from '../modal/partner-staffs/UpdatePartnerStaffModal.tsx';

interface PartnerStaffTableProps {
  partnerId: string;
  staffs: PartnerStaff[];
  isLoading: boolean;
  hidden: boolean;
}

export default function PartnerStaffTable({
  partnerId,
  staffs,
  isLoading,
  hidden,
}: PartnerStaffTableProps) {
  const records: PartnerStaff[] = staffs;
  const queryClient = useQueryClient();
  const { mutate: updatePartnerStaff, isPending: updatePartnerStaffIsPending } =
    useUpdatePartnerStaff();
  const { mutate: voidPartnerStaff, isPending: voidPartnerStaffIsPending } =
    useVoidPartnerStaff();

  const [selectedStaff, setSelectedStaff] = useState<PartnerStaff | null>(null);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);

  const columns: DataTableColumn<PartnerStaff>[] = [
    {
      title: '人員名稱',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'name',
      width: 230,
    },
    {
      title: 'Email',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'email',
      width: 230,
    },
    {
      title: '帳號',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'username',
      width: 230,
    },
    {
      title: '廠商後台權限',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'role',
      textAlign: 'left',
      width: 230,
      render: (row) => (row.role === 'Viewer' ? '檢視者' : '編輯者'),
    },
    {
      accessor: '',
      titleStyle: { color: 'gray', fontWeight: 'normal', textAlign: 'center' },
      width: 120,
      textAlign: 'right',
      render: (row) => (
        <span style={{ paddingRight: '20px' }}>
          <EditButton partnerId={partnerId} row={row} />
          <DeleteButton partnerId={partnerId} row={row} />
        </span>
      ),
    },
  ];

  function EditButton({ partnerId, row }) {
    return (
      <ActionIcon
        variant="transparent"
        size="sm"
        aria-label="edit"
        ml="xs"
        onClick={() => onClickEditButton(partnerId, row)}
      >
        <IconEdit size={16} color={'#141414'} />
      </ActionIcon>
    );
  }

  function onClickEditButton(partnerId: string, row: PartnerStaff) {
    setSelectedStaff(row);
    openEditModal();
  }

  function onConfirmUpdatePartnerStaff(
    partnerStaffId: string,
    formValues: UpdatePartnerStaffForm,
  ) {
    if (updatePartnerStaffIsPending) return;

    updatePartnerStaff(
      { id: partnerStaffId, ...formValues },
      {
        onSuccess: () => {
          closeEditModal();
          notifySuccess('更新後台管理員成功');
          queryClient.invalidateQueries({
            queryKey: ['partnerStaffs', partnerId],
          });
        },
      },
    );
  }

  function DeleteButton({
    partnerId,
    row,
  }: {
    partnerId: string;
    row: PartnerStaff;
  }) {
    return (
      <ActionIcon
        variant="transparent"
        size="sm"
        aria-label="edit"
        ml="xs"
        onClick={() => onClickDeleteButton(partnerId, row)}
      >
        <IconTrash size={16} color={'#FA5252'} />
      </ActionIcon>
    );
  }

  function onClickDeleteButton(partnerId, row) {
    modals.openContextModal({
      modal: 'confirmModal',
      title: '刪除後台管理員',
      closeOnEscape: true,
      closeOnClickOutside: true,
      innerProps: {
        modalBody: `確定要刪除該後台管理員(${row.email})嗎?`,
        loading: voidPartnerStaffIsPending,
        onConfirm: () => onConfirmDeletePartnerStaff(partnerId, row.id),
      },
    });

    function onConfirmDeletePartnerStaff(
      partnerId: string,
      partnerStaffId: string,
    ) {
      if (voidPartnerStaffIsPending) return;

      voidPartnerStaff(partnerStaffId, {
        onSuccess: () => {
          modals.closeAll();
          notifySuccess('刪除後台管理員成功');
          queryClient.invalidateQueries({
            queryKey: ['partnerStaffs', partnerId],
          });
        },
      });
    }
  }

  return (
    <>
      {selectedStaff && editModalOpened && (
        <UpdatePartnerStaffModal
          opened={editModalOpened}
          onClose={closeEditModal}
          row={selectedStaff}
          isPending={updatePartnerStaffIsPending}
          onConfirm={({ formValues }) =>
            onConfirmUpdatePartnerStaff(selectedStaff.id, formValues)
          }
        />
      )}
      <Box hidden={hidden}>
        <DataTable
          idAccessor="id"
          highlightOnHover
          minHeight={150}
          columns={columns}
          records={records}
          verticalSpacing="sm"
          noRecordsText="目前還沒有任何人員，點擊「新增人員」開始建立！"
          fetching={isLoading}
        />
      </Box>
    </>
  );
}
